/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback } from 'react';
import { ChevronDown, Folder, Loader2, Plus, Share, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useTranslation } from 'react-i18next';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { useTheme } from 'components/core/theme-provider';
import { useToast } from 'hooks/use-toast';
import { MAX_FILE_SIZE } from 'constant/file';
import ConfigurationButton from 'components/configuration/ConfigurationButton';

const AddDropdownMenu = ({
  onFileUpload,
  onFolderCreate,
  isMobile = false,
  isButtonHide,
}: {
  onFileUpload?: (files: File[]) => void;
  onFolderCreate?: (folderName: string) => void;
  isMobile?: boolean;
  isButtonHide?: boolean | 'undefined';
}) => {
  const { t } = useTranslation();
  const isMediaLibrary = useIsMediaLibrary();
  const { theme } = useTheme();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const { toast } = useToast();

  type UploadedFile = {
    id: number;
    name: string;
    size: string;
    file: File;
  };

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [folderName, setFolderName] = useState('');
  const btnHide = isButtonHide === 'undefined' ? true : isButtonHide;
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleCreateFolder = () => {
    setIsCreateFolderModalOpen(true);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    processFiles(files);
    event.target.value = '';
  };

  const processFiles = useCallback(
    (files: File[]) => {
      const MAX_FILES = 10;
      const ALLOWED_TYPES = ['.pdf', '.docx', '.txt', '.html', '.json', '.xlsx', '.csv', '.vsdx', '.pptx', '.ppt'];

      // Calculate how many more files can be added
      const remainingSlots = MAX_FILES - uploadedFiles.length;

      if (remainingSlots <= 0) {
        toast({
          title: t('FILE_SELECTION_LIMIT_REACHED') || "File Selection Limit Reached",
          description: t('MAXIMUM_FILE_SELECTED_DESCRIPTION') || "You can only upload up to 10 files at once.",
          variant: "destructive",
        });
        return;
      }

      const validFiles: File[] = [];
      let skippedCount = 0;
      let oversizedCount = 0;

      files.forEach((file) => {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ALLOWED_TYPES.includes(fileExtension)) {
          skippedCount++;
        } else if (file.size > MAX_FILE_SIZE) {
          oversizedCount++;
        } else {
          validFiles.push(file);
        }
      });

      if (skippedCount > 0) {
        toast({
          title: t("UNSUPPORTED_FILES_SKIPPED") || "Unsupported Files Skipped",
          description: `${skippedCount} ${t("UNSUPPORTED_FILES_SKIPPED_DESCRIPTION")}`,
          variant: "destructive",
        });
      }

      if (oversizedCount > 0) {
        toast({
          title: t('FILE_SIZE_LIMIT_EXCEEDED') || 'File size limit exceeded',
          description:
            oversizedCount === 1
              ? t('ONE_FILE_EXCEEDS_SIZE_LIMIT', { defaultValue: 'File exceeds the 30MB limit.' })
              : t('FILES_EXCEED_SIZE_LIMIT', {
                  defaultValue: '{{count}} files exceed the 30MB limit.',
                  count: oversizedCount,
                }),
          variant: 'destructive',
        });
      }

      if (validFiles.length > remainingSlots) {
        toast({
          title: t("FILE_LIMIT_EXCEED") || "File Limit Exceeded",
          description: t('MAXIMUM_FILE_SELECTED_DESCRIPTION') || "You can only upload up to 10 files at once.",
          variant: "destructive",
        });
        return;
      }

      if (validFiles.length > 0) {
        const newFiles = validFiles.map((file, index) => {
          const path = (file as any).webkitRelativePath || file.name;
          return {
            id: Date.now() + index,
            name: path.split(/[/\\]/).pop() || file.name,
            size: formatFileSize(file.size),
            file: file,
          };
        });
        setUploadedFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [uploadedFiles, toast, t]
  );

  const handleDrop: React.DragEventHandler = useCallback(
    (event) => {
      event.preventDefault();
      if (isUploading) return;
      const files = Array.from(event.dataTransfer.files);
      processFiles(files);
    },
    [processFiles, isUploading]
  );

  const handleDragOver: React.DragEventHandler = useCallback((event) => {
    event.preventDefault();
    if (isUploading) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }, [isUploading]);

  const handleDragLeave: React.DragEventHandler = useCallback((event) => {
    event.preventDefault();
  }, []);

  const removeFile = (fileId: number) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleUpload = async () => {
    if (uploadedFiles.length > 0 && onFileUpload) {
      // Create new File objects with only the base name to strip all directory metadata (webkitRelativePath)
      const filesToUpload = uploadedFiles.map((f) => {
        const originalFile = f.file;
        const path = (originalFile as any).webkitRelativePath || originalFile.name;
        const baseName = path.split(/[/\\]/).pop() || originalFile.name;

        return new (File as any)([originalFile], baseName, { type: originalFile.type }) as File;
      });

      setIsUploading(true);
      await onFileUpload(filesToUpload);
      setIsUploading(false);
    }

    setIsUploadModalOpen(false);
    setUploadedFiles([]);
  };

  const handleCancel = () => {
    setIsUploadModalOpen(false);
    setUploadedFiles([]);
  };

  const handleCreateFolderSubmit = () => {
    if (folderName.trim() && onFolderCreate) {
      onFolderCreate(folderName.trim());
    }

    setIsCreateFolderModalOpen(false);
    setFolderName('');
  };

  const handleCreateFolderCancel = () => {
    setIsCreateFolderModalOpen(false);
    setFolderName('');
  };

  const handleFolderNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCreateFolder();
    }
  };
  return (
    <div className={isMediaLibrary ? 'w-full sm:w-auto' : undefined}>
      {isMediaLibrary ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ConfigurationButton icon={<Plus className="h-4 w-4" />}>
              {t('ADD_NEW', { defaultValue: 'Add New' })}
              <ChevronDown className="h-4 w-4 opacity-90" />
            </ConfigurationButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8}>
            <DropdownMenuItem onClick={handleCreateFolder}>
              <Folder />
              <span>{t('FOLDER', { defaultValue: 'Folder' })}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        btnHide && (
          <Button
            size="sm"
            className={`text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed display-none bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
            onClick={handleFileUpload}
          >
            <Share className="h-4 w-4" />
            {!isMobile && <span className="">{t('UPLOAD')}</span>}
          </Button>
        )
      )}

      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">{t('UPLOAD_FILES')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div
              className={`border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg sm:p-0 md:p-4 text-center transition-colors w-full bg-transparent ${isUploading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="flex flex-col items-center gap-4">
                <Upload className="h-4 w-4 text-gray-400" />
                <div className="space-y-2">
                  <p className={theme === 'dark' ? "text-lg text-gray" : "text-lg text-gray-900"}>
                    {t('DRAG_AND_DROP_DESCRIPTION')}
                  </p>
                  <p className={theme === 'dark' ? "text-sm text-gray break-words" : "text-sm text-gray-500 break-words"}>
                    {t('FILE_TYPES_DESCRIPTION')}
                  </p>
                  <p className={theme === 'dark' ? "text-sm text-gray break-words" : "text-sm text-gray-500 break-words"}>
                    {t('FILE_SIZE_DESCRIPTION') || 'Up to 10 files, maximum 30MB each'}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2 mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    className="w-full sm:w-auto"
                    onClick={() => {
                      if (isUploading) return;
                      const input = document.getElementById('file-input');
                      if (input) input.click();
                    }}
                  >
                    <span className="truncate">{t("SELECT_FILES")}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    className="w-full sm:w-auto"
                    onClick={() => {
                      if (isUploading) return;
                      const input = document.getElementById('folder-input');
                      if (input) input.click();
                    }}
                  >
                    <span className="truncate">{t("SELECT_FOLDER")}</span>
                  </Button>
                </div>

                <Input
                  id="file-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInput}
                  accept=".pdf,.docx,.txt,.html,.xlsx,.vsdx,.pptx,.json,.csv"
                  disabled={isUploading}
                />
                <Input
                  id="folder-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInput}
                  {...({ webkitdirectory: "", directory: "" } as any)}
                  disabled={isUploading}
                />
              </div>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <h4 className={`text-sm font-medium ${theme === "dark" ? "text-gray" : "text-gray-700"}`}>
                  {t("SELECTED_FILES")} ({uploadedFiles.length})
                </h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-background rounded-lg border"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className="flex flex-col flex-1 min-w-0 w-0">
                          <p
                            className={`text-sm font-medium  ${theme === 'dark' ? 'text-typography-white' : 'typography-black-primary'} truncate`}
                            title={file.name}
                          >
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {file.size}
                          </p>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        title={t('REMOVE_FILE', { defaultValue: 'Remove file' })}
                        disabled={isUploading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button disabled={isUploading} variant="outline" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploadedFiles.length === 0}
                className={`bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
              >
                {t('UPLOAD')} {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                {isUploading && <Loader2 className="animate-spin" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Folder Modal */}
      <Dialog open={isCreateFolderModalOpen} onOpenChange={setIsCreateFolderModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {t('CREATE_NEW_FOLDER', { defaultValue: 'Create new folder' })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="folder-name" className="text-sm font-medium">
                {t('NAME_OF_THE_FOLDER', { defaultValue: 'Name of the folder' })}*
              </Label>
              <Input
                id="folder-name"
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={handleFolderNameKeyDown}
                placeholder={t('ENTER_FOLDER_NAME', { defaultValue: 'Enter folder name' })}
                className="w-full"
                autoFocus
              />
              {folderName.trim() && (
                <p className="text-xs text-gray-500">
                  {t('FOLDER_WILL_BE_CREATED_AS', {
                    defaultValue: 'Folder will be created as: "{{name}}"',
                    name: folderName.trim(),
                  })}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleCreateFolderCancel}>
                {t('CANCEL')}
              </Button>
              <Button
                onClick={handleCreateFolderSubmit}
                disabled={!folderName.trim()}
              >
                {t('CREATE')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddDropdownMenu;